import { Button, IconButton, Stack, Typography, TextField as MuiTextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import { FormProvider, Table, TextField } from '../../../../../components';
import { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { request } from '../../../../../utils';

const TABLE_HEADER = [
  { header: 'Service Name', accessor: 'service_name' },
  { header: 'Rate', accessor: 'rate' }
];

export const PhysiotherapyBookingDetailForm = ({ onClose, onAdd }) => {
  const [serviceDropdown, setServiceDropdown] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [filteredServices, setFilteredServices] = useState([]);

  const fetchServices = useCallback(async () => {
    const response = await request('/physiotherapy-service-master-list', { method: 'GET' });
    if (response?.success) {
      setServiceDropdown(response.data || []);
      setFilteredServices(response.data || []);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  useEffect(() => {
    const lower = searchInput.toLowerCase();
    setFilteredServices(
      serviceDropdown.filter((s) => s.service_name?.toLowerCase().includes(lower))
    );
  }, [searchInput, serviceDropdown]);

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        service: Yup.string().required('Please select a service'),
        quantity: Yup.number().required().min(1)
      })
    ),
    defaultValues: { service: '', service_name: '', quantity: 1, rate: 0, amount: 0, gst_per: 0, gst_amt: 0, disc_amt: 0, net_amt: 0 },
    mode: 'all'
  });

  const { handleSubmit, setValue, watch, reset } = methods;
  const quantity = watch('quantity');
  const rate = watch('rate');

  useEffect(() => {
    const qty = Number(quantity) || 0;
    const r = Number(rate) || 0;
    const amount = qty * r;
    setValue('amount', amount);
    setValue('net_amt', amount);
  }, [quantity, rate, setValue]);

  const handleServiceRowClick = (row) => {
    setValue('service', row._id);
    setValue('service_name', row.service_name);
    setValue('rate', row.rate || 0);
    setValue('gst_per', row.gst_per || 0);
  };

  const formSubmitHandler = (formValues) => {
    const qty = Number(formValues.quantity) || 1;
    const r = Number(formValues.rate) || 0;
    const amount = qty * r;
    const gst_amt = parseFloat(((amount * (formValues.gst_per || 0)) / 100).toFixed(2));
    onAdd({
      ...formValues,
      quantity: qty,
      amount,
      gst_amt,
      net_amt: amount,
      disc_amt: 0
    });
    reset();
    onClose();
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(formSubmitHandler)}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor="grey.300"
        pb={1}
        mb={1}
      >
        <Typography variant="h4" fontWeight="bold">
          Add Physiotherapy Service
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon color="error" fontSize="large" />
        </IconButton>
      </Stack>

      <MuiTextField
        fullWidth
        size="small"
        placeholder="Search service..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 1 }}
      />

      <Table
        columns={TABLE_HEADER}
        data={filteredServices}
        size="small"
        onRowClick={handleServiceRowClick}
        containerSX={{ maxHeight: '35vh', overflow: 'auto' }}
      />

      <Stack spacing={1} mt={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Service</Typography>
          <TextField name="service_name" sx={{ flex: 0.7 }} disabled placeholder="Selected service" />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Qty / Rate</Typography>
          <TextField name="quantity" sx={{ flex: 0.35 }} type="number" inputProps={{ min: '1' }} placeholder="Qty" />
          <TextField name="rate" sx={{ flex: 0.35 }} type="number" inputProps={{ min: '0' }} placeholder="Rate" disabled />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ flex: 0.3, fontWeight: 700, fontSize: '1.4rem' }}>Amount</Typography>
          <TextField name="amount" sx={{ flex: 0.7 }} disabled placeholder="Amount" />
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="flex-end" mt={2} spacing={1}>
        <Button variant="contained" type="submit">Add</Button>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
      </Stack>
    </FormProvider>
  );
};
